import {
  Box,
  Typography,
  Button,
  styled,
  CircularProgress,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import Webcam from "react-webcam";
import InfoIcon from "@mui/icons-material/Info";
import color from "../../components/utils/Colors";
import { useDropzone } from "react-dropzone";
import { uploadProfileImage } from "../../services/services";
import { useSnackbar } from "../../components/utils/SnackBar";

const PreviewImage = styled("img")({
  borderRadius: "50%",
  width: 150,
  height: 150,
  objectFit: "cover",
});

const PhotoContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: 20,
});

const Notification = styled(Typography)({
  backgroundColor: color.secondColor,
  padding: 10,
  marginTop: 20,
  fontSize: "14px",
  width: "100%",
  color: color.firstColor,
  border: "solid 1px",
  borderColor: color.borderColor,
  borderRadius: "4px",
});

const videoConstraints = {
  facingMode: "user",
};

const ProfilePicture = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const isSmallScreen2 = useMediaQuery("(max-width:1200px)");
  const showSnackbar = useSnackbar();
  const [activeTab, setActiveTab] = useState<"upload" | "takePhoto">("upload");
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(
    'Camera loaded. Click "Capture" to take a photo.',
  );
  const [files] = useState<any[]>([]);

  const webcamRef = useRef<Webcam | null>(null);

  const handleTabChange = (tab: "upload" | "takePhoto") => {
    setActiveTab(tab);
  };

  const handleCapture = useCallback(() => {
    setLoading(true);
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setImageSrc(imageSrc);
      setNotification(
        'Your profile photo is ready. Click "Save" to use this photo.',
      );
    }
    setLoading(false);
  }, [webcamRef]);

  const [file, setFile] = useState<File | null>(null);

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".png", ".jpg"],
    },
    onDrop: (acceptedFiles) => {
      setFile(acceptedFiles[0]);
    },
  });
  const handleSave = async () => {
  if (!file) {
    showSnackbar("Please select an image first");
    return;
  }

  try {
    setLoading(true);

    const formData = new FormData();
    formData.append("image", file);

    const res = await uploadProfileImage(formData);

    console.log(res.data);

    showSnackbar("Profile image uploaded successfully");
  } catch (error: any) {
    showSnackbar(
      error?.response?.data?.message || "Upload failed"
    );
  } finally {
    setLoading(false);
  }
};
  return (
    <>
      <Box
        sx={(theme) => ({
          marginTop: "25px",
          flex: 1,
          p: "3%",
          borderRadius: "16px",
          bgcolor:
            theme.palette.mode === "light"
              ? "rgba(255, 255, 255, 0.4)"
              : "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(2px)",
        })}
      >
        <Box
          sx={{
            flex: 1,
            p: 4,
            padding: isSmallScreen ? "0%" : "3%",
            pt: 0,
            paddingBottom: "10%",
          }}
        >
          <Box sx={{ borderRadius: "16px" }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                color: color.textColor,
                fontWeight: "bold",
                textAlign: "center",
                textUnderlineOffset: "4px",
                textDecoration: "underline",
                width: "fit-content",
                margin: "auto",
                padding: "4px 16px",
                borderRadius: "4px",
                mb: 4,
              }}
            >
              Edit Profile Photo
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "transparent",
                boxShadow: "0px 4px 10px #00000010",
                borderRadius: 1,
                marginBottom: "35px",
                marginTop: "35px",
                height: "auto",
                paddingLeft: "0px",
              }}
            >
              <Box
                sx={{
                  bgcolor: color.firstColor,
                  borderRadius: "4px 0px 0px 4px",
                  p: 1,
                  paddingTop: 1.5,
                  paddingBottom: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "auto",
                }}
              >
                <InfoIcon sx={{ color: "white" }} />
              </Box>
              <Typography sx={{ marginLeft: 2, fontSize: "14px" }}>
                Your profile photo will be used on your profile and throughout
                the site.
              </Typography>
            </Box>

            <div>
              <div>
                <Box display="flex" mt={2}>
                  <Button
                    id="button"
                    style={{
                      textTransform: "none",
                      fontSize: "14px",
                      background:
                        activeTab === "upload" ? color.firstColor : "white",
                      border: "solid 1px",
                      borderColor: color.borderColor,
                      color:
                        activeTab === "upload" ? "white" : color.firstColor,
                      borderRadius: "4px",
                    }}
                    onClick={() => handleTabChange("upload")}
                  >
                    Upload
                  </Button>
                  <Button
                    id="button"
                    style={{
                      textTransform: "none",
                      fontSize: "14px",
                      background:
                        activeTab === "takePhoto" ? color.firstColor : "white",
                      border: "solid 1px",
                      borderColor: color.borderColor,
                      color:
                        activeTab === "takePhoto" ? "white" : color.firstColor,
                      borderRadius: "4px",
                      marginLeft: "10px",
                    }}
                    onClick={() => handleTabChange("takePhoto")}
                  >
                    Take Photo
                  </Button>
                </Box>
                <PhotoContainer>
                  {activeTab === "upload" && (
                    <div style={{ width: "100%" }}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={2}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{ m: 2, gap: 10, width: "100%" }}
                        >
                          {file && (
                            <Box mt={2}>
                              <Typography
                                sx={{
                                  textAlign: "center",
                                  fontWeight: "bold",
                                  mb: 2,
                                  p: 2,
                                  borderRadius: "8px",
                                  color: color.textColor,
                                }}
                                variant="body1"
                              >
                                Selected file Preview
                              </Typography>
                              <img
                                src={URL.createObjectURL(file)}
                                alt="Preview"
                                width={200}
                              />
                            </Box>
                          )}
                          <Box
                            sx={{
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                color: "white",
                                borderRadius: "8px",
                                cursor: "pointer",
                                width: "80%",
                                alignSelf: "center",
                                margin: "auto",
                                // maxWidth: '700px',
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "center",
                                mt: 4,
                              }}
                              {...getRootProps()}
                              border="1.5px dashed"
                              borderColor={color.borderColor}
                              padding={4}
                              // width={400}
                              height={250}
                            >
                              <input {...getInputProps()} />
                              <Typography
                                textAlign={"center"}
                                sx={{
                                  p: 2,
                                  fontSize: "18px",
                                  borderRadius: "8px",
                                  color: color.firstColor,
                                }}
                              >
                                Drag 'n' drop some files here or click to select
                                files
                              </Typography>
                            </Box>
                            {/* <Typography
                              sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: "8px",
                                color: color.textColor,
                              }}
                              border="2px solid"
                              borderColor={color.borderColor}
                              variant="body1"
                            >
                              Selected file:
                              {file && <> {file.name}</>}
                            </Typography> */}
                          </Box>
                        </Box>
                        <Button
                          onClick={handleSave}
                          id="button"
                          style={{
                            background: color.firstColor,
                            color: "white",
                            borderRadius: "8px",
                            marginTop: 20,
                          }}
                          type="submit"
                          color="primary"
                          // onClick={() => alert("Profile picture saved!")}
                        >
                          Save
                        </Button>
                      </Box>
                    </div>
                  )}
                  {activeTab === "takePhoto" && (
                    <>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: isSmallScreen2 ? "column" : "row",
                        }}
                      >
                        {loading ? (
                          <CircularProgress />
                        ) : (
                          <Webcam
                            audio={false}
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            style={{
                              objectFit: "cover",
                              width: "100%",
                              maxHeight: "400px",
                              height: "auto",
                              maxWidth: "100%",
                              float: "left",
                            }}
                            videoConstraints={videoConstraints}
                          />
                        )}
                        <div
                          style={{
                            paddingLeft: isSmallScreen ? "0%" : "5%",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              border: "dotted 2px #bbbbbb",
                              marginTop: "20px",
                              height: 150,
                              width: 150,
                              borderRadius: "50%",
                            }}
                          >
                            {imageSrc && (
                              <PreviewImage
                                src={imageSrc}
                                alt="Circular Preview"
                              />
                            )}
                          </div>
                          <Box mt={2} sx={{ display: "flex", gap: "10px" }}>
                            <Button
                              id="button"
                              style={{
                                background: color.firstColor,
                                color: "white",
                                marginTop: 1,
                                textTransform: "none",
                                width: "fit-content",
                              }}
                              onClick={handleCapture}
                              type="submit"
                              variant="contained"
                              color="primary"
                            >
                              Capture
                            </Button>

                            <Button
                              id="button"
                              style={{
                                background: color.firstColor,
                                color: "white",
                                marginTop: 1,
                                textTransform: "none",
                                width: "fit-content",
                              }}
                              onClick={handleSave}
                              type="submit"
                              variant="contained"
                              color="primary"
                            >
                              Save
                            </Button>
                          </Box>
                        </div>
                      </div>

                      <Notification variant="body1">
                        {notification}
                      </Notification>
                    </>
                  )}
                </PhotoContainer>
              </div>
            </div>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ProfilePicture;
